import type {
  CrudServiceListResponse,
  HttpService,
  ResultList,
  Searchable,
  Sortable,
} from '@empathyco/playboard-core-domain'
import type { ContextProvider } from '../../../providers/interfaces'
import type { PlatformEqualizeEntity } from '../adapters/platform-equalize.adapter'
import type { EqualizeDTO } from '../adapters/types/equalize-dto'
import type { EqualizeAdapter } from '../adapters/types/equalize.adapter'
import type { EqualizeCrudService, EqualizeCrudServiceListParams } from './types'
import { interpolate } from '@empathyco/x-adapter'
import { inject, injectable, named, optional } from 'inversify'
import { TYPES } from '../../../config/inversify.types'
import { FeatureIdEnum } from '../../../enums'

@injectable()
export class PlatformEqualizeCrudService implements EqualizeCrudService {
  public constructor(
    @inject(TYPES.HttpService) protected httpService: HttpService<PlatformEqualizeEntity>,
    @inject(TYPES.EqualizeAdapter)
    protected adapter: EqualizeAdapter<PlatformEqualizeEntity | undefined>,
    @inject(TYPES.EqualizeUrl) protected url: string,
    @inject(TYPES.ContextProvider) protected contextProvider: ContextProvider,
    @inject(TYPES.Endpoint)
    @named(FeatureIdEnum.EQUALIZE)
    @optional()
    protected endpoint = '/configurations/clients/{instance}/features/groups/equalize{(/)id}',
  ) {}

  async list(params: EqualizeCrudServiceListParams): Promise<CrudServiceListResponse<EqualizeDTO>> {
    const requestSort = params.sort ? this.getRequestSort(params.sort) : undefined
    try {
      const response = await this.httpService.get<ResultList<PlatformEqualizeEntity>>(
        this.buildRequestUrl(),
        {
          addAccessToken: true,
          parameters: {
            ...params.page,
            ...(requestSort && {
              'sort.field': requestSort.sort.field,
              'sort.direction': requestSort.sort.direction,
            }),
            ...(params.search && this.getRequestSearch(params.search)),
            ...params.filters,
            defaultFlag: params.isDefault,
          },
        },
      )
      return {
        results: response.data?.results.map(result => this.adapter.decode(result)) ?? [],
        numFound: response.data?.numFound ?? 0,
      }
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async create(item: EqualizeDTO): Promise<EqualizeDTO> {
    try {
      const response = await this.httpService.post(this.buildRequestUrl(), {
        addAccessToken: true,
        body: this.adapter.encode(item),
      })
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async read(id: EqualizeDTO['id']): Promise<EqualizeDTO> {
    try {
      const response = await this.httpService.get(this.buildRequestUrl({ id }), {
        addAccessToken: true,
      })
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async update(item: EqualizeDTO): Promise<EqualizeDTO> {
    try {
      const response = await this.httpService.put(this.buildRequestUrl({ id: item.id }), {
        addAccessToken: true,
        body: this.adapter.encode(item),
      })
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async delete(item: EqualizeDTO): Promise<void> {
    try {
      const response = await this.httpService.delete(this.buildRequestUrl({ id: item.id }), {
        addAccessToken: true,
      })
      return response.data
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  private buildRequestUrl(parameters: Record<string, any> = {}): string {
    const url = `${this.url}${this.endpoint}`
    const { instanceId: instance } = this.contextProvider()
    if (!instance) {
      throw new Error('instance is not defined')
    }
    return interpolate(url, { instance, ...parameters })
  }

  private getRequestSort(sortParam: Sortable['sort']): Sortable | undefined {
    return sortParam.field ? { sort: sortParam } : undefined
  }

  private getRequestSearch(search: Searchable['search']): { query: string } | undefined {
    const { query } = search
    if (!query) {
      return undefined
    }
    return {
      query,
    }
  }
}
